import type { HTMLAttributes } from 'react';

import { Badge } from '../components/badge';
import { cn } from '../lib/cn';

export interface BottleneckFlagProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Localized "Knelpunt" label (this component is taal-neutraal — the caller translates). */
  label: string;
  /** Localized, pre-formatted stilstand-teller (e.g. "10 producten"). */
  countLabel: string;
}

/**
 * Story 3.3 (FR8, UXR4) — de knelpunt-markering: amber `warning`-badge + stilstand-teller,
 * gerenderd op PRECIES één stap per canoniek proces (of nul, bij nul stilstand — de aanroeper
 * beslist WANNEER dit component rendert, dit component zelf heeft geen nul-state).
 * SM-C1-scheiding: dit is amber (`Badge variant="warning"`, zelfde tokens als `--warning-light`/
 * `--warning-text`), expliciet NIET het gestreepte/muted geen-procesdata-uiterlijk
 * (`StatusBadge`'s `coverage-no-data`/`coverage-publisher-offline` varianten) — de twee mogen
 * nooit met elkaar verward worden.
 *
 * POLISH-FOLLOWUP (Epic 3 retro, opgelost in Epic 4): `Badge` is `inline-flex` (default
 * `flex-wrap: nowrap`) — in een smalle `StatusStrip`-tegel (`min-w-[120px]`) liet dat de teller
 * ("43 producten") over de tegelrand heen lopen. `flex-wrap` hier laat de teller naar een eigen
 * regel ONDER het label wrappen zodra de ruimte krap is, zonder de ruimere `FunnelBar`-context
 * (waar dit component ook leeft) te raken — daar blijft alles gewoon op één regel passen.
 */
export function BottleneckFlag({ className, countLabel, label, ...props }: BottleneckFlagProps) {
  return (
    <Badge
      {...props}
      className={cn('flex-wrap gap-x-1.5 gap-y-0', className)}
      data-testid="bottleneck-flag"
      variant="warning"
    >
      <svg aria-hidden="true" className="size-3 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path d="M12 3 2 20h20L12 3Z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 9v4m0 4h.01" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span>{label}</span>
      <span className="font-normal" data-testid="bottleneck-flag-count">{countLabel}</span>
    </Badge>
  );
}
